/**
 * What the reverse trial says — the mock's state 1 and its matrix rows "trial · days 1–11" and
 * "trial · days 12–14". Pure, beside `billing-states.ts`. Plan §2 U61.
 */
import { PLANS, trialDaysLeft, trialPhase, type TrialPhase } from "@packagepage/shared";

import { dayName } from "./billing-states.js";
import { count } from "./usage-meter.js";

/** `orgs.current`'s trial fields. */
export interface TrialStanding {
  readonly trialEndsAt?: string | null;
  readonly trialUsage?: {
    readonly batchLevelLinks: number;
    readonly extraLanguages: number;
  } | null;
}

export function phaseOf(standing: TrialStanding, now: Date): TrialPhase {
  const ends = standing.trialEndsAt ?? null;
  return trialPhase(ends === null ? null : new Date(ends), now);
}

/** Whether the screen shows the trial's card in place of the plan's. */
export function trialRunning(standing: TrialStanding, now: Date): boolean {
  const phase = phaseOf(standing, now);
  return phase === "quiet" || phase === "closing";
}

export function daysLeftOf(standing: TrialStanding, now: Date): number {
  const ends = standing.trialEndsAt ?? null;
  return ends === null ? 0 : trialDaysLeft(new Date(ends), now);
}

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

/** One feature row of state 1: what the organisation uses, or "not yet". */
export interface TrialUsageRow {
  readonly label: string;
  readonly using: number | null;
}

/** Import and the API have no subject yet, so they read "not yet" — an honest absence. */
export function trialUsageRows(standing: TrialStanding): readonly TrialUsageRow[] {
  const usage = standing.trialUsage ?? null;
  return [
    { label: "Batch-level links", using: usage?.batchLevelLinks ?? 0 },
    { label: "Extra languages", using: usage?.extraLanguages ?? 0 },
    { label: "Spreadsheet import", using: null },
    { label: "API and webhooks", using: null },
  ];
}

export interface TrialAttention {
  readonly kind: "trial-closing";
  readonly title: string;
  readonly body: string;
}

/**
 * The closing banner, days 12–14, with this organisation's own counts. It names what changes
 * on Launch today and claims no gate that is not built.
 */
export function trialAttention(
  standing: TrialStanding,
  now: Date,
  gtinsActive: number | null,
): TrialAttention | null {
  const ends = standing.trialEndsAt ?? null;
  if (ends === null || phaseOf(standing, now) !== "closing") return null;
  const links = standing.trialUsage?.batchLevelLinks ?? 0;
  const allowance = PLANS.launch.activeGtins;
  const gtins =
    gtinsActive === null
      ? ""
      : ` Launch includes ${count(allowance)} active GTINs, and you have ${count(gtinsActive)}.`;
  const kept =
    links === 0
      ? ""
      : ` Your ${count(links)} batch-level ${plural(links, "link keeps", "links keep")} working.`;
  return {
    kind: "trial-closing",
    title: `Your Growth trial ends on ${dayName(new Date(ends))}`,
    body:
      `Every printed code keeps resolving after it ends. On Launch your pages carry a ` +
      `"Powered by packagepage" line, and fair use is ${count(PLANS.launch.fairUsePerMonth ?? 0)} ` +
      `scans a month, counted from that day.${gtins}${kept} Billing & usage has the plans.`,
  };
}
